import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  ClipboardList, Warehouse, ScanBarcode, ScanLine, History, Users, LogOut, User, 
  ChevronDown, BarChart3, FolderCog, Tag, ClipboardCheck, Settings, 
  FileSpreadsheet, Lock, AlertTriangle, ShoppingCart, Truck, FileText, HelpCircle,
  Menu, X, Bell, Receipt, Flag
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useStocktaking } from "@/context/StocktakingContext";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, hasPageAccess } = useAuth();
  const { isStocktakingActive, stocktakingLocation, showExitWarning, requestNavigation, confirmExit, cancelExit } = useStocktaking();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Low stock alerts state
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [lowStockSettings, setLowStockSettings] = useState({ threshold: 2, activity_days: 30 });
  const [alertsOpen, setAlertsOpen] = useState(false);
  
  // Old bookings warning state (bookings >= 90 days old)
  const OLD_BOOKING_DAYS = 90;
  const [oldBookings, setOldBookings] = useState([]);
  const [oldBookingsCount, setOldBookingsCount] = useState(0);
  const [oldBookingsOpen, setOldBookingsOpen] = useState(false);

  // Fetch low stock alerts
  const fetchLowStockAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/inventory/low-stock-alerts`);
      setLowStockAlerts(response.data.items || []);
      setLowStockCount(response.data.count || 0);
      if (response.data.settings) {
        setLowStockSettings(response.data.settings);
      }
    } catch (error) {
      console.error("Error fetching low stock alerts:", error);
    }
  }, []);

  // Fetch old bookings (>= 90 days)
  const fetchOldBookings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/inventory/old-bookings?days=${OLD_BOOKING_DAYS}`);
      setOldBookings(response.data.bookings || []);
      setOldBookingsCount(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching old bookings:", error);
    }
  }, []);
  
  // Fetch alerts on mount and periodically
  useEffect(() => {
    if (user) {
      fetchLowStockAlerts();
      fetchOldBookings();
      // Refresh every 5 minutes
      const interval = setInterval(() => {
        fetchLowStockAlerts();
        fetchOldBookings();
      }, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user, fetchLowStockAlerts, fetchOldBookings]);
  
  // Define navigation structure with permission keys
  // Main nav items - core functions visible based on role
  const coreNavItems = [
    { path: "/inventory", label: "Lager", icon: Warehouse, permissionKey: "lager" },
    { path: "/scan", label: "Skanna", icon: ScanBarcode, permissionKey: "skanna" },
    { path: "/picking", label: "Plocklista", icon: ClipboardList, permissionKey: "plocklista" },
    { path: "/scan-order", label: "Skanna order", icon: ScanLine, permissionKey: "plocklista" },
    { path: "/purchasing", label: "Inköp", icon: ShoppingCart, permissionKey: "inkop" },
    { path: "/order-cost", label: "Orderkostnad", icon: Receipt, permissionKey: "statistik" },
    { path: "/deliveries", label: "Leveranser", icon: Truck, permissionKey: "plocklista" },
  ];
  
  // Driver/delivery specific - shows for lagerpersonal and chauffor
  const driverNavItems = [
    { path: "/ai-scan", label: "Följesedel", icon: FileText, permissionKey: "skanna_foljesedel" },
  ];
  
  // Admin menu items
  const adminMenuItems = [
    { path: "/history", label: "Historik", icon: History, permissionKey: "administration" },
    { path: "/statistics", label: "Statistik", icon: BarChart3, permissionKey: "statistik" },
    { path: "/stocktaking", label: "Inventering", icon: ClipboardCheck, permissionKey: "inventering" },
    { path: "/maintenance", label: "Kategorier", icon: Tag, permissionKey: "administration" },
    { path: "/import-export", label: "Import/Export", icon: FileSpreadsheet, permissionKey: "administration" },
    { path: "/help/workflows", label: "Hjälp", icon: HelpCircle, permissionKey: "lager" },
  ];
  
  // Filter items based on permissions
  const filterByPermission = (items) => items.filter(item => hasPageAccess(item.permissionKey));
  
  const visibleCoreItems = filterByPermission(coreNavItems);
  const visibleDriverItems = filterByPermission(driverNavItems);
  const visibleAdminItems = filterByPermission(adminMenuItems);
  
  // Combine main nav items (core + driver items that user has access to)
  const mainNavItems = [...visibleCoreItems];
  
  // Add driver items to main nav if user has access
  if (visibleDriverItems.length > 0) {
    // For chauffor role - only show ai-scan (Följesedel)
    const chaufforOnlyItems = visibleDriverItems.filter(item => item.permissionKey === "skanna_foljesedel");
    if (chaufforOnlyItems.length > 0 && !hasPageAccess("lager")) {
      // Chauffor only sees Följesedel in main nav
      mainNavItems.push(...chaufforOnlyItems);
    } else if (visibleDriverItems.length > 0) {
      // Lagerpersonal sees both Leverans and Följesedel
      mainNavItems.push(...visibleDriverItems);
    }
  }
  
  // Show admin menu if user has access to any admin items
  const showAdminMenu = visibleAdminItems.length > 0;

  // Check if any admin sub-item is active
  const isAdminActive = visibleAdminItems.some(
    item => location.pathname === item.path || location.pathname.startsWith(item.path + "/")
  );

  // Handle navigation with stocktaking lock check
  const handleNavClick = (e, path) => {
    if (isStocktakingActive && path !== "/scan") {
      if (e && typeof e.preventDefault === 'function') {
        e.preventDefault();
      }
      requestNavigation(path);
      return;
    }
    setMobileMenuOpen(false);
  };

  // Handle confirm exit from stocktaking
  const handleConfirmExit = () => {
    const path = confirmExit();
    if (path) {
      navigate(path);
    }
  };

  // Render a nav link with optional lock indicator
  const NavLink = ({ path, label, icon: Icon, className, showLabel = true, onClick }) => {
    const isActive = location.pathname === path || location.pathname.startsWith(path + "/");
    const isLocked = isStocktakingActive && path !== "/scan";
    
    const content = (
      <>
        <Icon className="h-5 w-5" />
        {showLabel && <span className="hidden sm:inline">{label}</span>}
        {isLocked && <Lock className="h-3 w-3 text-amber-500" />}
      </>
    );

    return (
      <Link
        to={path}
        onClick={(e) => { handleNavClick(e, path); onClick?.(); }}
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm font-medium",
          isActive 
            ? "bg-primary/10 text-primary" 
            : "text-muted-foreground hover:text-foreground hover:bg-muted",
          isLocked && "opacity-60",
          className
        )}
        data-testid={`nav-${label.toLowerCase().replace(/[åäö ]/g, '-')}`}
      >
        {content}
      </Link>
    );
  };

  // Mobile menu link component
  const MobileNavLink = ({ path, label, icon: Icon }) => {
    const isActive = location.pathname === path || location.pathname.startsWith(path + "/");
    const isLocked = isStocktakingActive && path !== "/scan";
    
    return (
      <Link
        to={path}
        onClick={(e) => handleNavClick(e, path)}
        className={cn(
          "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
          isActive 
            ? "bg-primary/10 text-primary" 
            : "text-muted-foreground hover:text-foreground hover:bg-muted",
          isLocked && "opacity-60"
        )}
        data-testid={`mobile-nav-${label.toLowerCase().replace(/[åäö ]/g, '-')}`}
      >
        <Icon className="h-5 w-5" />
        <span>{label}</span>
        {isLocked && <Lock className="h-4 w-4 text-amber-500 ml-auto" />}
      </Link>
    );
  };

  return (
    <>
      <nav className="bg-card border-b border-border z-50">
        {/* Stocktaking active banner */}
        {isStocktakingActive && (
          <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2">
            <div className="container mx-auto flex items-center justify-center gap-2 text-sm text-amber-600 dark:text-amber-400">
              <Lock className="h-4 w-4" />
              <span className="font-medium">Inventeringsläge aktivt</span>
              <span className="text-muted-foreground">({stocktakingLocation})</span>
              <span className="hidden sm:inline">- Slutför inventeringen för att låsa upp navigering</span>
            </div>
          </div>
        )}

        <div className="container mx-auto px-2">
          <div className="flex items-center justify-between py-2">
            {/* Desktop navigation */}
            <div className="hidden md:flex items-center gap-1">
              {mainNavItems.map((item) => (
                <NavLink key={item.path} {...item} />
              ))}

              {/* Administration dropdown - only show if user has access */}
              {showAdminMenu && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm font-medium h-auto",
                        isAdminActive 
                          ? "bg-primary/10 text-primary" 
                          : "text-muted-foreground hover:text-foreground hover:bg-muted",
                        isStocktakingActive && "opacity-60"
                      )}
                      data-testid="nav-admin"
                    >
                      <FolderCog className="h-5 w-5" />
                      <span className="hidden sm:inline">Administration</span>
                      {isStocktakingActive && <Lock className="h-3 w-3 text-amber-500" />}
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuLabel>Administration</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {visibleAdminItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
                      const isLocked = isStocktakingActive && item.path !== "/scan";
                      return (
                        <DropdownMenuItem 
                          key={item.path}
                          onClick={() => {
                            if (isLocked) {
                              requestNavigation(item.path);
                            } else {
                              navigate(item.path);
                            }
                          }}
                          className={cn(
                            "flex items-center gap-2 cursor-pointer",
                            isActive && "bg-primary/10 text-primary",
                            isLocked && "opacity-60"
                          )}
                          data-testid={`nav-${item.label.toLowerCase().replace(/[åäö ]/g, '-')}`}
                        >
                          <Icon className="h-5 w-5" />
                          <span>{item.label}</span>
                          {isLocked && <Lock className="h-3 w-3 text-amber-500" />}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Mobile hamburger menu button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0">
                <SheetHeader className="p-4 border-b">
                  <SheetTitle className="text-left">Meny</SheetTitle>
                </SheetHeader>
                <div className="py-4 space-y-1">
                  {/* Main navigation items */}
                  <div className="px-2 mb-2">
                    <p className="text-xs font-medium text-muted-foreground px-4 mb-2">HUVUDMENY</p>
                    {mainNavItems.map((item) => (
                      <MobileNavLink key={item.path} {...item} />
                    ))}
                  </div>
                  
                  {/* Admin items */}
                  {showAdminMenu && (
                    <div className="px-2 pt-2 border-t">
                      <p className="text-xs font-medium text-muted-foreground px-4 mb-2 mt-2">ADMINISTRATION</p>
                      {visibleAdminItems.map((item) => (
                        <MobileNavLink key={item.path} {...item} />
                      ))}
                    </div>
                  )}
                  
                  {/* User management for admins */}
                  {isAdmin && (
                    <div className="px-2 pt-2 border-t">
                      <p className="text-xs font-medium text-muted-foreground px-4 mb-2 mt-2">SYSTEM</p>
                      <MobileNavLink path="/users" label="Användare" icon={Users} />
                      <MobileNavLink path="/settings" label="Inställningar" icon={Settings} />
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>

            {/* User menu and notifications - grouped together */}
            <div className="flex items-center gap-1">
              {/* User menu */}
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-2" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <span className="hidden sm:inline font-medium">
                        {user.display_name || user.username}
                      </span>
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div>
                        <p className="font-medium">{user.display_name || user.username}</p>
                        <p className="text-xs text-muted-foreground">@{user.username}</p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {isAdmin && (
                      <>
                        <DropdownMenuItem 
                          onClick={() => {
                            if (isStocktakingActive) {
                              requestNavigation("/users");
                            } else {
                              navigate("/users");
                            }
                          }}
                          className={cn("flex items-center gap-2 cursor-pointer", isStocktakingActive && "opacity-60")} 
                          data-testid="nav-users"
                        >
                          <Users className="h-4 w-4" />
                          Hantera användare
                          {isStocktakingActive && <Lock className="h-3 w-3 text-amber-500 ml-auto" />}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => {
                            if (isStocktakingActive) {
                              requestNavigation("/settings");
                            } else {
                              navigate("/settings");
                            }
                          }}
                          className={cn("flex items-center gap-2 cursor-pointer", isStocktakingActive && "opacity-60")} 
                          data-testid="nav-settings"
                        >
                          <Settings className="h-4 w-4" />
                          Inställningar
                          {isStocktakingActive && <Lock className="h-3 w-3 text-amber-500 ml-auto" />}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem 
                      onClick={() => {
                        if (isStocktakingActive) {
                          requestNavigation("/admin");
                        } else {
                          logout();
                        }
                      }} 
                      className={cn("text-destructive cursor-pointer", isStocktakingActive && "opacity-60")} 
                      data-testid="logout-btn"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Logga ut
                      {isStocktakingActive && <Lock className="h-3 w-3 text-amber-500 ml-auto" />}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              {/* Old bookings warning flag (bookings >= 90 days old) */}
              {user && hasPageAccess("lager") && (
                <Popover open={oldBookingsOpen} onOpenChange={setOldBookingsOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="relative"
                      title="Gamla bokningar (90+ dagar)"
                      data-testid="old-bookings-flag-btn"
                    >
                      <Flag className={cn("h-5 w-5", oldBookingsCount > 0 && "text-amber-500")} />
                      {oldBookingsCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-amber-500 text-black text-xs flex items-center justify-center font-bold" data-testid="old-bookings-count">
                          {oldBookingsCount > 99 ? "99+" : oldBookingsCount}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-80 p-0">
                    <div className="p-3 border-b">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Flag className="h-4 w-4 text-amber-500" />
                        Gamla bokningar
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        Bokningar som är {OLD_BOOKING_DAYS} dagar eller äldre – behöver troligen följas upp
                      </p>
                    </div>
                    <div className="max-h-[320px] overflow-y-auto">
                      {oldBookings.length === 0 ? (
                        <div className="p-4 text-center text-muted-foreground text-sm" data-testid="old-bookings-empty">
                          Inga gamla bokningar
                        </div>
                      ) : (
                        <div className="divide-y">
                          {oldBookings.map((b) => (
                            <Link
                              key={b.id}
                              to={`/inventory/${b.ean}`}
                              onClick={() => setOldBookingsOpen(false)}
                              className="flex items-start justify-between gap-2 p-3 hover:bg-muted transition-colors"
                              data-testid={`old-booking-${b.id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-mono text-sm font-medium truncate">
                                  {b.artikelnummer || b.ean}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {b.produktnamn || b.comment || "—"}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {b.quantity} st
                                  {b.customer_order_number ? ` · ${b.customer_order_number}` : ""}
                                  {b.booked_by ? ` · ${b.booked_by}` : ""}
                                </p>
                              </div>
                              <span className="ml-1 shrink-0 px-2 py-1 rounded text-xs font-bold bg-amber-500/10 text-amber-600">
                                {b.age_days != null ? `${b.age_days} d` : "90+ d"}
                              </span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              {/* Low stock alerts notification - right next to user menu */}
              {user && hasPageAccess("lager") && (
                <Popover open={alertsOpen} onOpenChange={setAlertsOpen}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="relative"
                      data-testid="low-stock-alerts-btn"
                    >
                      <Bell className="h-5 w-5" />
                      {lowStockCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold">
                          {lowStockCount > 99 ? "99+" : lowStockCount}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-80 p-0">
                    <div className="p-3 border-b">
                      <h4 className="font-semibold flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                        Lågt lager
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        Produkter med ≤{lowStockSettings.threshold} st som rört på sig senaste {lowStockSettings.activity_days} dagarna
                      </p>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      {lowStockAlerts.length === 0 ? (
                        <div className="p-4 text-center text-muted-foreground text-sm">
                          Inga produkter med lågt lager
                        </div>
                      ) : (
                        <div className="divide-y">
                          {lowStockAlerts.map((item) => (
                            <Link
                              key={item.id}
                              to={`/inventory/${item.ean}`}
                              onClick={() => setAlertsOpen(false)}
                              className="flex items-center justify-between p-3 hover:bg-muted transition-colors"
                              data-testid={`alert-item-${item.ean}`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-mono text-sm font-medium truncate">
                                  {item.artikelnummer}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {item.produktnamn || item.tillverkare || "Okänd produkt"}
                                </p>
                              </div>
                              <div className={cn(
                                "ml-2 px-2 py-1 rounded text-xs font-bold",
                                item.total_antal === 0 
                                  ? "bg-red-500/10 text-red-500" 
                                  : "bg-amber-500/10 text-amber-500"
                              )}>
                                {item.total_antal} st
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
        </div>
        
        {/* Mobile bottom navigation - simplified */}
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 md:hidden">
          <div className="flex items-center justify-around py-2">
            {/* Show max 5 items in bottom nav */}
            {mainNavItems.slice(0, 4).map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || 
                location.pathname.startsWith(item.path + "/");
              const isLocked = isStocktakingActive && item.path !== "/scan";
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={(e) => handleNavClick(e, item.path)}
                  className={cn(
                    "flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors text-xs font-medium relative",
                    isActive 
                      ? "bg-primary/10 text-primary" 
                      : "text-muted-foreground hover:text-foreground hover:bg-muted",
                    isLocked && "opacity-60"
                  )}
                  data-testid={`mobile-bottom-${item.label.toLowerCase()}`}
                >
                  <div className="relative">
                    <Icon className="h-5 w-5" />
                    {isLocked && (
                      <Lock className="h-2.5 w-2.5 text-amber-500 absolute -top-1 -right-1" />
                    )}
                  </div>
                  <span className="truncate max-w-[60px]">{item.label}</span>
                </Link>
              );
            })}
            
            {/* More button for admin menu */}
            {showAdminMenu && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      "flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors text-xs font-medium relative",
                      isAdminActive 
                        ? "bg-primary/10 text-primary" 
                        : "text-muted-foreground hover:text-foreground hover:bg-muted",
                      isStocktakingActive && "opacity-60"
                    )}
                    data-testid="mobile-more-btn"
                  >
                    <div className="relative">
                      <FolderCog className="h-5 w-5" />
                      {isStocktakingActive && (
                        <Lock className="h-2.5 w-2.5 text-amber-500 absolute -top-1 -right-1" />
                      )}
                    </div>
                    <span>Mer</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" side="top" className="w-56 mb-2">
                  <DropdownMenuLabel>Fler alternativ</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {/* Extra main items if more than 4 */}
                  {mainNavItems.slice(4).map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    const isLocked = isStocktakingActive && item.path !== "/scan";
                    return (
                      <DropdownMenuItem 
                        key={item.path} 
                        onClick={() => {
                          if (isLocked) {
                            requestNavigation(item.path);
                          } else {
                            navigate(item.path);
                          }
                        }}
                        className={cn(
                          "flex items-center gap-2 cursor-pointer",
                          isActive && "bg-primary/10 text-primary",
                          isLocked && "opacity-60"
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                        {isLocked && <Lock className="h-3 w-3 text-amber-500 ml-auto" />}
                      </DropdownMenuItem>
                    );
                  })}
                  {mainNavItems.length > 4 && <DropdownMenuSeparator />}
                  {/* Admin items */}
                  {visibleAdminItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    const isLocked = isStocktakingActive && item.path !== "/scan";
                    return (
                      <DropdownMenuItem 
                        key={item.path} 
                        onClick={() => {
                          if (isLocked) {
                            requestNavigation(item.path);
                          } else {
                            navigate(item.path);
                          }
                        }}
                        className={cn(
                          "flex items-center gap-2 cursor-pointer",
                          isActive && "bg-primary/10 text-primary",
                          isLocked && "opacity-60"
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                        {isLocked && <Lock className="h-3 w-3 text-amber-500 ml-auto" />}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </nav>

      {/* Exit stocktaking warning dialog */}
      <AlertDialog open={showExitWarning} onOpenChange={cancelExit}>
        <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Inventering pågår
            </AlertDialogTitle>
            <AlertDialogDescription>
              Du har en pågående inventering på lagerplats{" "}
              <span className="font-mono font-bold">{stocktakingLocation}</span>.
              <br /><br />
              Om du lämnar nu kommer inventeringen att avbrytas. Eventuella inskannade produkter har redan sparats.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
            <AlertDialogAction 
              onClick={handleConfirmExit}
              className="w-full sm:w-auto order-1 sm:order-2 bg-amber-500 hover:bg-amber-600"
            >
              Avbryt inventering & lämna
            </AlertDialogAction>
            <AlertDialogCancel className="w-full sm:w-auto order-2 sm:order-1">
              Fortsätt inventera
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
